import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { BotResponse, KnowledgeSource, MessageThread, Reference, UploadedAsset } from '../../types/chatTypes'
import { useAgents } from '../agents/useAgents'
import { useChatStream } from './useChatStream'

// Helper to create a default BotResponse
const createInitialBotResponse = (
  tempId: string,
  knowledgeSource: KnowledgeSource,
  isPrimary = false,
): BotResponse => ({
  message_id: tempId,
  knowledgeSource,
  is_sensitive: undefined,
  answer: '',
  isStreaming: true,
  needsFeedback: true,
  isPrimary,
  isMinimized: !isPrimary, // Primary response is expanded, others are minimized
})

interface UseChatLogicProps {
  existingChatId?: string // For existing chats only
  initialMessageThreads?: MessageThread[]
  onChatEstablished?: (chatId: string) => void
  onMessageSentSuccessfully?: (messageId: string, chatId: string) => void
  isNewChatSession?: boolean
  agentId?: string
}

export const useChatLogic = ({
  existingChatId,
  initialMessageThreads = [],
  onChatEstablished,
  onMessageSentSuccessfully,
  isNewChatSession = false,
  agentId,
}: UseChatLogicProps) => {
  const { currentAgent } = useAgents()

  // Chat ID management - use existing chat ID or generate new one
  const [currentChatId, setCurrentChatId] = useState(() => existingChatId || uuidv4())

  const [conversation, setConversation] = useState<MessageThread[]>(() => {
    return initialMessageThreads.length > 0 ? initialMessageThreads : []
  })
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false) // Tracks if a message send operation is in progress
  const [currentPhase, setCurrentPhase] = useState<{
    tempId: string
    phase: 'internal_streaming' | 'llm_resumed'
  } | null>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const isFirstMessageSentRef = useRef(false)

  // Function to check if conversation contains any documents/attachments
  const hasDocumentsInConversation = useMemo(() => {
    return conversation.some(thread => thread.attachedAsset)
  }, [conversation])

  // Update current chat ID when existing chat ID changes (for navigation between existing chats)
  useEffect(() => {
    if (existingChatId && existingChatId !== currentChatId) {
      setCurrentChatId(existingChatId)
    }
  }, [existingChatId, currentChatId])

  const regenerateChatId = useCallback(() => {
    const newId = uuidv4()
    setCurrentChatId(newId)
    return newId
  }, [])

  // Clear conversation function for explicit clearing
  const clearConversation = useCallback(() => {
    setConversation([])
  }, [])

  const clearConversationAndRegenerateChatId = useCallback(() => {
    setConversation([])
    return regenerateChatId()
  }, [regenerateChatId])

  useEffect(() => {
    if (conversation.length === 0 && initialMessageThreads.length > 0) {
      setConversation(initialMessageThreads)
    }

    isFirstMessageSentRef.current = !isNewChatSession && conversation.length > 0
  }, [initialMessageThreads, isNewChatSession, conversation.length])

  const handleStreamingUpdate = useCallback(
    (
      partialMessage: string,
      tempId: string,
      messageId?: string,
      error?: Error,
      knowledgeSource?: KnowledgeSource,
      errorDetails?: { policy_violations?: string[] },
      responseSource?: 'internal_knowledge' | 'agent_llm',
      isSensitive?: boolean,
    ) => {
      if (error?.message === 'USER_INPUT_CONTENT_VIOLATION') {
        setConversation(prevConversation => {
          const updated = prevConversation.map(thread => {
            if (thread.tempId === tempId) {
              return {
                ...thread,
                inputValidationError: error,
                inputValidationErrorDetails: errorDetails,
                // Clear any answers since the input was invalid
                answers: [],
              }
            }
            return thread
          })
          return updated
        })
        setIsSendingMessage(false)
        return
      }

      // Handle normal streaming updates and other errors
      setConversation(prevConversation =>
        prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          const updatedAnswers = [...thread.answers]
          let answerIndex = -1

          if (responseSource) {
            // Find existing answer by response source
            if (responseSource === 'internal_knowledge') {
              answerIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            } else if (responseSource === 'agent_llm') {
              answerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')
            }

            // If no answer exists for this source, create one dynamically
            if (answerIndex === -1) {
              const newKnowledgeSource: KnowledgeSource =
                responseSource === 'internal_knowledge' ? 'INTERNAL_KB' : knowledgeSource || 'LLM_ONLY'
              const newAnswer = createInitialBotResponse(
                `${tempId}-${responseSource}`,
                newKnowledgeSource,
                responseSource === 'internal_knowledge', // Internal knowledge is primary when it appears
              )

              // Add new answer - internal knowledge goes at end, agent at beginning
              if (responseSource === 'internal_knowledge') {
                updatedAnswers.push(newAnswer) // Add internal at end
                answerIndex = updatedAnswers.length - 1

                // When internal knowledge appears, minimize the existing GPT response
                if (updatedAnswers.length === 2) {
                  updatedAnswers[0] = { ...updatedAnswers[0], isMinimized: true, isPrimary: false }
                }
              } else {
                updatedAnswers.unshift(newAnswer) // Add agent at beginning
                answerIndex = 0
              }
            }
          } else {
            // Fallback for responses without source - update the first/only answer
            answerIndex = 0
          }

          if (answerIndex === -1 || answerIndex >= updatedAnswers.length) return thread

          const targetAnswer = updatedAnswers[answerIndex]

          // Update the answer with new content
          updatedAnswers[answerIndex] = {
            ...targetAnswer,
            message_id: messageId || targetAnswer.message_id,
            answer: error ? targetAnswer.answer : partialMessage,
            isStreaming: !error,
            error: error?.message,
            errorDetails: errorDetails,
            knowledgeSource: knowledgeSource || targetAnswer.knowledgeSource,
            is_sensitive: isSensitive !== undefined ? isSensitive : targetAnswer.is_sensitive,
          }

          return { ...thread, answers: updatedAnswers }
        }),
      )
      if (error) {
        setIsSendingMessage(false) // Stream ended due to error
      }
    },
    [],
  )

  const handleResponseTransition = useCallback(
    (
      tempId: string,
      responseSource: 'internal_knowledge' | 'agent_llm',
      phase: 'internal_streaming' | 'llm_resumed',
    ) => {
      setCurrentPhase({ tempId, phase })

      setConversation(prevConversation =>
        prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          const updatedAnswers = [...thread.answers]

          if (phase === 'internal_streaming') {
            // Internal knowledge found - minimize existing LLM response and add internal response
            const llmAnswerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')

            if (llmAnswerIndex !== -1) {
              // Minimize the existing LLM response but keep it streaming
              updatedAnswers[llmAnswerIndex] = {
                ...updatedAnswers[llmAnswerIndex],
                isMinimized: true,
                isPrimary: false,
                isStreaming: true, // Keep streaming for when LLM resumes
              }
            }

            // Add internal knowledge response if it doesn't exist
            const internalIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            if (internalIndex === -1) {
              const internalAnswer = createInitialBotResponse(`${tempId}-internal_knowledge`, 'INTERNAL_KB', true)
              updatedAnswers.push(internalAnswer)
            }
          } else if (phase === 'llm_resumed') {
            // LLM streaming resumed - ensure LLM answer is ready to continue streaming
            const llmAnswerIndex = updatedAnswers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')

            if (llmAnswerIndex !== -1) {
              // Ensure LLM answer is still streaming and minimized
              updatedAnswers[llmAnswerIndex] = {
                ...updatedAnswers[llmAnswerIndex],
                isStreaming: true,
                isMinimized: true,
                isPrimary: false,
              }
            }

            // Internal answer remains primary but no longer streaming
            const internalIndex = updatedAnswers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
            if (internalIndex !== -1) {
              updatedAnswers[internalIndex] = {
                ...updatedAnswers[internalIndex],
                isStreaming: false, // Internal is done when LLM resumes
                isPrimary: true,
                isMinimized: false,
              }
            }
          }

          return { ...thread, answers: updatedAnswers }
        }),
      )
    },
    [],
  )

  const handleMessageComplete = useCallback(
    (
      completedMessage: string,
      tempId: string,
      messageId: string,
      isSensitive: boolean,
      knowledgeSource?: KnowledgeSource,
      references?: Reference[],
    ) => {
      setConversation(prevConversation => {
        return prevConversation.map(thread => {
          if (thread.tempId !== tempId) return thread

          // Find the answer that matches this completion
          let answerIndex = -1

          // First try to find by exact message_id match
          answerIndex = thread.answers.findIndex(a => a.message_id === messageId)

          // If not found by exact ID, fall back to knowledge source matching
          if (answerIndex === -1) {
            if (thread.answers.length > 1) {
              // For dual responses, find by knowledge source
              if (knowledgeSource === 'INTERNAL_KB') {
                answerIndex = thread.answers.findIndex(a => a.knowledgeSource === 'INTERNAL_KB')
              } else {
                // Find the non-internal knowledge answer (LLM response)
                answerIndex = thread.answers.findIndex(a => a.knowledgeSource !== 'INTERNAL_KB')
              }
            } else {
              // For single responses
              answerIndex = 0
            }
          }

          if (answerIndex === -1) return thread

          const updatedAnswers = [...thread.answers]

          // Update the answer with completion data
          updatedAnswers[answerIndex] = {
            ...updatedAnswers[answerIndex],
            message_id: messageId,
            is_sensitive: isSensitive,
            answer: completedMessage,
            isStreaming: false,
            error: undefined,
            knowledgeSource: knowledgeSource || updatedAnswers[answerIndex].knowledgeSource,
            references: references && references.length > 0 ? references : updatedAnswers[answerIndex].references,
          }

          return { ...thread, answers: updatedAnswers }
        })
      })

      // Only set sending to false when all answers are done streaming
      const allAnswersComplete = conversation.every(thread => thread.answers.every(answer => !answer.isStreaming))

      if (allAnswersComplete) {
        setIsSendingMessage(false)
      }

      if (onMessageSentSuccessfully) {
        onMessageSentSuccessfully(messageId, currentChatId)
      }

      if (isNewChatSession && !isFirstMessageSentRef.current && onChatEstablished) {
        onChatEstablished(currentChatId)
        isFirstMessageSentRef.current = true
      }
    },
    [onMessageSentSuccessfully, currentChatId, isNewChatSession, onChatEstablished, conversation],
  )

  const handleDisclaimersReceived = useCallback(
    (disclaimers: any[]) => {
      setConversation(prevConversation => {
        if (prevConversation.length === 0) return prevConversation

        const lastThreadIndex = prevConversation.length - 1
        const lastThread = prevConversation[lastThreadIndex]

        if (lastThread.answers.length === 0) return prevConversation

        const updatedAnswers = [...lastThread.answers]
        updatedAnswers[updatedAnswers.length - 1] = {
          ...updatedAnswers[updatedAnswers.length - 1],
          disclaimers,
        }

        const updatedConversation = [...prevConversation]
        updatedConversation[lastThreadIndex] = { ...lastThread, answers: updatedAnswers }

        return updatedConversation
      })
    },
    [],
  )

  // Internet search only if the agent is configured with that tool
  const isInternetSearchOnly = useMemo(() => currentAgent?.tools?.includes('INTERNET_SEARCH'), [currentAgent?.tools])
  const isInternalSearchOnly = useMemo(
    () => (currentAgent?.slug || '').toLowerCase() === 'internal-data',
    [currentAgent?.slug],
  )

  // ----- Single Stream Configuration -----
  const streamConfig = {
    chatId: currentChatId,
    agentId: agentId,
  }

  // Single unified stream that handles both internal knowledge and agent responses
  const {
    isLoading,
    error: streamError,
    sendMessage: streamSendMessage,
    dualResponse,
    currentResponseSource,
  } = useChatStream(streamConfig, handleMessageComplete, handleStreamingUpdate, handleResponseTransition, undefined, handleDisclaimersReceived)

  const isStreamActive = isLoading

  const sendMessage = useCallback(
    (message: string, attachedAsset?: UploadedAsset) => {
      if (!message.trim() || isSendingMessage || isStreamActive || currentAgent?.id !== agentId) return // Prevent sending if already processing

      setIsTyping(false)
      setIsSendingMessage(true) // Indicate that a send operation has started

      const assetId = attachedAsset?.id

      const tempId = `temp-${uuidv4()}`

      // Start with a single placeholder response - we'll add more dynamically based on backend streaming
      // Use the user-selected knowledge source or default based on agent type
      const knowledgeSource: KnowledgeSource = isInternetSearchOnly
        ? 'INTERNET_SEARCH'
        : isInternalSearchOnly
        ? 'INTERNAL_KB'
        : 'LLM_ONLY'
      const initialAnswer = createInitialBotResponse(tempId, knowledgeSource, true)

      const thread: MessageThread = {
        tempId,
        question: message,
        answers: [initialAnswer],
        attachedAsset: attachedAsset,
      }

      setConversation(prev => [...prev, thread])

      // Send message through single stream - backend handles dual strategy
      streamSendMessage(message, tempId, currentAgent, assetId)

      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
        }
      }, 100)
    },
    [
      streamSendMessage,
      chatContainerRef,
      currentAgent,
      isSendingMessage,
      isStreamActive,
      agentId,
      isInternetSearchOnly,
      isInternalSearchOnly,
    ],
  )

  const handleRetry = useCallback(
    (question: string, tempIdToRetry: string, searchKnowledgeSource?: KnowledgeSource) => {
      setConversation(prev => prev.filter(thread => thread.tempId !== tempIdToRetry))
      // Use a timeout to ensure state update is processed before resending
      setTimeout(() => {
        sendMessage(question)
      }, 0)
    },
    [sendMessage],
  )

  const toggleResponseMinimized = useCallback((threadTempId: string, responseMessageId: string) => {
    setConversation(prevConversation =>
      prevConversation.map(thread => {
        if (thread.tempId !== threadTempId) return thread

        const updatedAnswers = thread.answers.map(answer => {
          if (answer.message_id === responseMessageId) {
            return { ...answer, isMinimized: !answer.isMinimized }
          }
          return answer
        })

        return { ...thread, answers: updatedAnswers }
      }),
    )
  }, [])

  useEffect(() => {
    if (chatContainerRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = chatContainerRef.current
      // Auto-scroll if user is near the bottom or if a message is actively being sent/streamed
      const PADDING_THRESHOLD = 150 // If user is within this many pixels from bottom, auto-scroll
      const isUserNearBottom = scrollHeight - scrollTop <= clientHeight + PADDING_THRESHOLD

      if (isUserNearBottom && (isSendingMessage || isStreamActive)) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
          }
        }, 100) // Delay to allow DOM updates
      }
    }
  }, [conversation, isSendingMessage, isStreamActive]) // Scroll on conversation changes or stream activity

  // Combined loading state for UI
  const combinedIsLoading = isSendingMessage || isStreamActive
  const isInputDisabled = combinedIsLoading

  return {
    conversation,
    setConversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    regenerateChatId,
    isTyping,
    setIsTyping,
    isLoading: combinedIsLoading, // Unified loading state
    error: streamError, // Expose error from the stream
    sendMessage,
    handleRetry,
    toggleResponseMinimized, // Function to expand/minimize responses
    chatContainerRef,
    isInputDisabled,
    currentChatId,
    hasDocumentsInConversation, // Expose this for use in QuestionInput
    dualResponse, // Expose dual response for components that need it
    currentResponseSource, // Expose current response source for real-time indicators
    currentPhase, // Expose current transition phase for UI indicators
  }
}
